import {
  BasketIcon,
  Box,
  CartIcon,
  ChartIcon,
  Divider,
  DocumentIcon,
  GKButton,
  GKTypography,
  Grid2,
  ManageIcon,
  RiskProfileIcon,
  Show,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TermIcon,
  Typography,
  UserIcon,
  VolatileIcon,
} from "components";
import { EquityBasketListInterface } from "interfaces/EquityBasket";
import { OrderSimulationTemplate } from "interfaces/OrderSimulation";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppSelector } from "store/store";
import { handleRemoveUnderscore } from "utils";
import { paletteLightTheme } from "utils/ColorTheme";
import { AccountType, handleCapitalize } from "utils/common";
import { matchDate, returnPositiveNegativeColor } from "utils/helpers";
import { numberToCurrency } from "utils/MathFunction";
import OrderSimulation from "./OrderSimulation";

interface Props {
  id?: any;
  key?: any;
  modelPortfolio?: EquityBasketListInterface;
  isFetching?: any;
  orderSimulationTemplate: OrderSimulationTemplate;
}

const EquityBasketCard = ({
  id: key,
  modelPortfolio,
  isFetching,
  orderSimulationTemplate,
}: Props) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [openAllocationModal, setOpenAllocationModal] = useState(false);

  const userInfo = useAppSelector((state) => state?.user?.userData);

  return (
    <>
      <Grid2
        key={key}
        container
        borderBottom={`solid 1px var(--neutral-100)`}
        pb={3}
        mr={2}
        spacing={2}
      >
        <Grid2 size={1} display={"flex"} justifyContent={"center"}>
          <img
            src={modelPortfolio?.basketLogo || `/Logos/errorImg.svg`}
            alt=""
            style={{
              borderRadius: 6,
              width: "66px",
              height: "66px",
              border: `solid 1px var(--neutral-100)`,
              padding: 4,
            }}
          />
        </Grid2>
        <Grid2 size={11} container spacing={1}>
          <Grid2 size={6} alignItems={"center"} display={"flex"}>
            <Typography fontSize={16} fontWeight={600}>
              {modelPortfolio?.name}
            </Typography>
          </Grid2>
          <Grid2
            size={6}
            display={"flex"}
            gap={1}
            justifyContent={"flex-end"}
            alignItems={"center"}
          >
            <GKButton
              variant="outlined"
              startIcon={<ManageIcon />}
              color="dark"
              size="small"
              sx={{
                visibility:
                  AccountType.ONLY_ONBOARDING === userInfo?.accountType
                    ? "hidden"
                    : "visible",
              }}
              onClick={() => {
                dispatch(TAB({ tabKey: "manageEquityBasketTab", tabValue: 0 }));
                navigate(`/app/equity-basket/${modelPortfolio?.id}/manage`);
              }}
            >
              Manage
            </GKButton>
            <GKButton
              variant="outlined"
              color="dark"
              startIcon={<BasketIcon />}
              size="small"
              onClick={() => {
                navigate(`/app/equity-basket/${modelPortfolio?.id}/`);
              }}
              sx={{
                minWidth: "unset",
              }}
            >
              Profile
            </GKButton>
          </Grid2>
          <Grid2 size={12} display={"flex"} alignItems={"center"} gap={2}>
            {[
              {
                title: "Advisor Type",
                logo: UserIcon,
                value: modelPortfolio?.managementStyle,
              },
              {
                title: "Basket Type",
                logo: BasketIcon,
                value: modelPortfolio?.basketType,
              },
            ]?.map((dataItem) => {
              return (
                <Box display={"flex"} alignItems={"center"} gap={0.5}>
                  <dataItem.logo color={"var(--neutral-500)"} />
                  <Typography color={"var(--neutral-500)"} fontSize={12}>
                    {dataItem.title} :
                  </Typography>
                  <Typography fontWeight={600} fontSize={12}>
                    {dataItem?.value}
                  </Typography>
                </Box>
              );
            })}
          </Grid2>
          <Show.When
            isTrue={
              !isEmpty(modelPortfolio?.riskProfile) ||
              !isEmpty(modelPortfolio?.volatility) ||
              !isEmpty(modelPortfolio?.timePeriod)
            }
          >
            <Grid2 size={12} spacing={2} container>
              {[
                {
                  name: "Risk",
                  value: modelPortfolio?.riskProfile[0] || "-",
                  icon: RiskProfileIcon,
                  withSeperator: true,
                  iconColor: `var(--warning-700)`,
                },
                {
                  name: "Volatility",
                  value: modelPortfolio?.volatility || "-",
                  icon: VolatileIcon,
                  withSeperator: true,
                  iconColor: "var(--orange-700)",
                },
                {
                  name: "Time Horizon",
                  value: modelPortfolio?.timePeriod || "-",
                  icon: TermIcon,
                  withSeperator: false,
                  iconColor: "var(--blue-700)",
                },
              ]?.map((item) => {
                return (
                  <Show.When isTrue={!isEmpty(item.value)}>
                    <Box display={"flex"} alignItems={"center"} gap={0.5}>
                      <Typography fontSize={12} color="textSecondary">
                        {item.name}:
                      </Typography>
                      <Box display={"flex"} alignItems={"center"} gap={0.5}>
                        <item.icon color={item.iconColor} />
                        <Typography fontSize={12} fontWeight={600}>
                          {handleRemoveUnderscore(
                            handleCapitalize(item?.value),
                          ) || "-"}
                        </Typography>
                      </Box>
                    </Box>
                    {item.withSeperator && (
                      <Divider orientation="vertical" sx={{ height: "20px" }} />
                    )}
                  </Show.When>
                );
              })}
            </Grid2>
          </Show.When>
          <Grid2 size={12}>
            <TableContainer key={key}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell
                      width={"16.6%"}
                      sx={{
                        padding: "16px !important",
                        width: "16.6%",
                        borderRight: `solid 1px ${paletteLightTheme.palette.border.main}`,
                      }}
                    >
                      <GKTypography
                        loading={isFetching}
                        fontSize={12}
                        color={"var(--neutral-500)"}
                      >
                        Portfolios Mapped
                      </GKTypography>
                      <GKTypography
                        loading={isFetching}
                        fontSize={14}
                        fontWeight={600}
                      >
                        {modelPortfolio.totalMappedPortfolios || "-"}
                      </GKTypography>
                    </TableCell>
                    <TableCell
                      width={"16.6%"}
                      sx={{
                        padding: "16px !important",
                        width: "16.6%",
                        borderRight: `solid 1px ${paletteLightTheme.palette.border.main}`,
                      }}
                    >
                      <GKTypography
                        loading={isFetching}
                        fontSize={12}
                        color={"var(--neutral-500)"}
                      >
                        AUM Mapped
                      </GKTypography>
                      <GKTypography
                        loading={isFetching}
                        fontSize={14}
                        fontWeight={600}
                      >
                        {numberToCurrency(modelPortfolio.mappedAum) || "-"}
                      </GKTypography>
                    </TableCell>
                    <TableCell
                      width={"16.6%"}
                      sx={{
                        padding: "16px !important",
                        width: "16.6%",
                        borderRight: `solid 1px ${paletteLightTheme.palette.border.main}`,
                      }}
                    >
                      <GKTypography
                        loading={isFetching}
                        fontSize={12}
                        color={"var(--neutral-500)"}
                      >
                        Revenue Mapped
                      </GKTypography>
                      <GKTypography
                        loading={isFetching}
                        fontSize={14}
                        fontWeight={600}
                      >
                        {numberToCurrency(modelPortfolio.mappedRevenue) || "-"}
                      </GKTypography>
                    </TableCell>
                    <TableCell
                      width={"16.6%"}
                      sx={{
                        padding: "16px !important",
                        width: "16.6%",
                        borderRight: `solid 1px ${paletteLightTheme.palette.border.main}`,
                      }}
                    >
                      <GKTypography
                        loading={isFetching}
                        fontSize={12}
                        color={"var(--neutral-500)"}
                      >
                        XIRR
                      </GKTypography>
                      <GKTypography
                        loading={isFetching}
                        fontSize={14}
                        fontWeight={600}
                        color={returnPositiveNegativeColor(modelPortfolio.xirr)}
                      >
                        {modelPortfolio.xirr || "-"}
                      </GKTypography>
                    </TableCell>
                    <TableCell
                      width={"16.6%"}
                      sx={{
                        width: "16.6%",
                        padding: "16px !important",
                      }}
                    >
                      <GKTypography
                        loading={isFetching}
                        fontSize={12}
                        color={"var(--neutral-500)"}
                      >
                        Last Updated on
                      </GKTypography>
                      <GKTypography
                        loading={isFetching}
                        fontSize={14}
                        fontWeight={600}
                      >
                        {matchDate(modelPortfolio.lastChange[1]) || "-"}
                      </GKTypography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={1}
          >
            <Show.When
              isTrue={modelPortfolio?.managementStyle !== "Subscription"}
            >
              <GKButton
                onClick={() => {
                  setOpenAllocationModal(true);
                }}
                size="small"
                variant="outlined"
                startIcon={<DocumentIcon />}
                color="dark"
                sx={{
                  visibility:
                    AccountType.ONLY_ONBOARDING === userInfo?.accountType
                      ? "hidden"
                      : "visible",
                }}
              >
                Generate Trades
              </GKButton>
            </Show.When>
            <GKButton
              onClick={() => {
                navigate(
                  `/app/equity-basket/${modelPortfolio?.id}/order-manager`,
                );
              }}
              sx={{
                mr: "auto",
                visibility:
                  AccountType.ONLY_ONBOARDING === userInfo?.accountType
                    ? "hidden"
                    : "visible",
              }}
              variant="outlined"
              color="dark"
              startIcon={<CartIcon />}
              size="small"
            >
              Order Management
            </GKButton>
            <GKButton
              variant="outlined"
              color="dark"
              startIcon={<ChartIcon />}
              size="small"
              sx={{ ml: "auto" }}
              onClick={() => {
                navigate(
                  `/app/equity-basket/${modelPortfolio?.id}/portfolio?basketType=${modelPortfolio?.managementStyle}`,
                );
              }}
            >
              {modelPortfolio?.managementStyle === "Subscription"
                ? "Subscribers"
                : "Portfolios"}
            </GKButton>
          </Grid2>
        </Grid2>
      </Grid2>

      {/* {openAllocationModal && (
        <EquityBasketAllocation
          open={openAllocationModal}
          setOpen={setOpenAllocationModal}
          modelData={modelPortfolio}
        />
      )} */}
      {openAllocationModal && (
        <OrderSimulation
          open={openAllocationModal}
          setOpen={setOpenAllocationModal}
          modelData={modelPortfolio}
          orderSimulationTemplate={orderSimulationTemplate}
        />
      )}
    </>
  );
};

export default EquityBasketCard;
